/**
 * Menu de contexto: as ações válidas para o que está sob o dedo (PRD §15.4).
 *
 * ## Por que ele não é opcional
 *
 * A gramática de clique já estava saturada antes de os verbos de mundo existirem: esquerdo
 * no próprio tile pega do chão, esquerdo em amistoso abre a loja, esquerdo em criatura
 * seleciona alvo, esquerdo no chão desmarca, direito em jogador propõe troca. Acrescentar
 * cinco verbos — olhar, usar, abrir, puxar, cavar — sem acrescentar **como executá-los**
 * seria escrever pacotes que ninguém consegue enviar.
 *
 * E é também a única resposta que funciona no celular, plataforma que §1 declara como alvo:
 * no toque o input é um direcional, não há botão direito, e o gesto equivalente é o toque
 * longo. Os dois abrem este mesmo menu.
 *
 * ## DOM, e não Phaser
 *
 * Pelo mesmo motivo que o resto da interface é DOM: texto selecionável, acessível ao leitor
 * de tela, e sem custo de textura. O menu vive por cima do canvas e some ao primeiro clique
 * fora dele.
 */

export interface ContextAction {
  label: string;
  run: () => void;
}

export class ContextMenu {
  private readonly root: HTMLElement;
  private open = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.root.className = 'context-menu';

    // Fechar ao clicar fora é obrigatório num menu que abre por gesto: sem isso, o menu
    // fica preso na tela e o clique seguinte cai nele em vez de no mundo.
    document.addEventListener('pointerdown', (event) => {
      if (!this.open) return;
      if (event.target instanceof Node && this.root.contains(event.target)) return;
      this.close();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });

    this.close();
  }

  get isOpen(): boolean {
    return this.open;
  }

  /**
   * Abre o menu num ponto da tela.
   *
   * A lista chega pronta: quem sabe quais ações valem para aquele alvo é a cena, e um menu
   * que decidisse isso sozinho precisaria conhecer o estado do mundo inteiro.
   */
  show(x: number, y: number, actions: readonly ContextAction[]): void {
    if (actions.length === 0) {
      this.close();
      return;
    }

    this.root.replaceChildren();

    for (const action of actions) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'context-menu-item';
      button.textContent = action.label;
      button.addEventListener('click', () => {
        this.close();
        action.run();
      });
      this.root.append(button);
    }

    this.open = true;
    this.root.classList.add('visible');

    // Posiciona primeiro e corrige depois: só com o menu medido dá para saber se ele
    // transborda a janela, e transbordar no celular é o caso comum e não a exceção.
    this.root.style.left = `${x}px`;
    this.root.style.top = `${y}px`;

    const box = this.root.getBoundingClientRect();
    if (box.right > window.innerWidth) {
      this.root.style.left = `${Math.max(0, window.innerWidth - box.width)}px`;
    }
    if (box.bottom > window.innerHeight) {
      this.root.style.top = `${Math.max(0, window.innerHeight - box.height)}px`;
    }
  }

  close(): void {
    this.open = false;
    this.root.classList.remove('visible');
    this.root.replaceChildren();
  }
}

/**
 * Toque longo: o equivalente do clique direito no celular.
 *
 * Meio segundo, e o movimento cancela — um toque que arrasta é o direcional de movimento,
 * não uma intenção de abrir menu, e confundir os dois faria o personagem parar de andar
 * toda vez que alguém segurasse a direção.
 */
export const LONG_PRESS_MS = 500;
export const LONG_PRESS_SLOP_PX = 12;
